<template>
    <div class="ah o-main-content-area" data-testid="assign-health-page">
        <div class="o-page-top-heading c-responsive-fixwidth">
            <h1 class="h1">Assign page health</h1>
            <p class="ah-lede">
                Every CI run and every 4-hourly sample, against its target. Read-only —
                this page reports the evidence, it does not produce it.
            </p>
        </div>

        <div class="ah-controls">
            <label>Window
                <select v-model.number="days" @change="load" data-testid="ah-days">
                    <option :value="1">24 hours</option>
                    <option :value="7">7 days</option>
                    <option :value="30">30 days</option>
                </select>
            </label>
            <label>Environment
                <select v-model="env" @change="load" data-testid="ah-env">
                    <!-- #1883 (ed, 13:57) — no `stage` option. stage- is a dev
                         box, not something to monitor, and the sampler no longer
                         writes it on the schedule. `?env=stage` still reads the
                         older files: accepting a value and offering it are
                         different decisions. -->
                    <option value="">private vs production</option>
                    <option value="private">private</option>
                    <option value="production">production</option>
                </select>
            </label>
            <button class="ah-refresh" @click="load" data-testid="ah-refresh">Refresh</button>
        </div>

        <p v-if="denied" class="ah-denied" data-testid="ah-denied">
            This page is Super Admin only.
        </p>
        <p v-else-if="error" class="ah-error" data-testid="ah-error">{{ error }}</p>

        <div v-else v-loading="loading">
            <!-- Header strip: the latest SAMPLE per environment. A CI run proves the
                 build; a sample proves the environment is still behaving between
                 builds, so the strip must not fall back to whichever record is
                 newest — a green CI run would mask a red sample. -->
            <div class="ah-strip" data-testid="ah-strip">
                <div v-for="e in stripEnvs" :key="e" class="ah-card"
                     :class="cardClass(latestSample[e])" :data-testid="'ah-strip-' + e">
                    <div class="ah-card-env">{{ e }}</div>
                    <div class="ah-card-verdict">{{ verdictWord(latestSample[e]) }}</div>
                    <div class="ah-card-meta">
                        <template v-if="latestSample[e]">
                            latest sample {{ shortTime(latestSample[e].at) }}
                            · {{ failedCount(latestSample[e]) }} of {{ judgedCount(latestSample[e]) }} judged checks failing<template
                                v-if="naCount(latestSample[e])"> · {{ naCount(latestSample[e]) }} n/a</template>
                        </template>
                        <template v-else>no sample in this window</template>
                    </div>
                    <div class="ah-card-meta" v-if="latestCi[e]">
                        latest CI {{ shortTime(latestCi[e].at) }} · {{ verdictWord(latestCi[e]) }}
                        <span v-if="latestCi[e].sha"> · {{ latestCi[e].sha }}</span>
                    </div>
                </div>
            </div>

            <!-- ⚠ #1883 (ed, 13:58) — THE COMPARISON IS private TG vs production TG.
                 Same fleet, same database, one target group carrying the cache
                 work and one without, so side by side they are the A/B that shows
                 what the work bought. Never against stage-, which is a different
                 machine and would make the machine the result.

                 ed could see pass/fail and nothing else, so a private row reading
                 FAIL looked like blanket failure — when the truth was a p95 of
                 5.27 s against a MEDIAN of 0.87 s. Both numbers are on one row
                 now, because they are one story. -->
            <h3 class="ah-h3">
                Latest sample — {{ comparisonEnvs.join(' vs ') }}
                <span class="ah-h3-note">{{ tgLabel }}</span>
            </h3>
            <p v-if="!comparison.length" class="ah-none" data-testid="ah-compare-none">No sample in this window.</p>
            <table class="ah-table ah-compare" v-else data-testid="ah-compare">
                <thead>
                    <tr>
                        <th>Check</th>
                        <th>Target</th>
                        <th v-for="e in comparisonEnvs" :key="e">{{ e }}</th>
                        <th v-if="comparisonEnvs.length === 2">Difference</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in comparison" :key="row.name" :data-testid="'ah-compare-' + row.name">
                        <td>
                            {{ row.name }}
                            <span class="ah-desc" v-if="row.desc">{{ row.desc }}</span>
                        </td>
                        <td class="ah-target">{{ row.op }} {{ row.target }}</td>
                        <td v-for="e in comparisonEnvs" :key="e" :class="verdictClass(row.cells[e])">
                            <template v-if="row.cells[e]">
                                {{ cellValue(row.cells[e]) }}
                                <span class="ah-verdict-word">{{ verdictWord(row.cells[e]) }}</span>
                                <!-- The median rides on its p95 row and keeps its own
                                     target and verdict: "p95 failed" and "the median
                                     is fine" are two facts, and showing only the
                                     worse one is what made a healthy tier read as
                                     broken. -->
                                <span v-if="row.cells[e].median" class="ah-median" :class="verdictClass(row.cells[e].median)">
                                    median {{ cellValue(row.cells[e].median) }} ({{ row.cells[e].median.op }} {{ row.cells[e].median.target }})
                                </span>
                                <span v-if="isNa(row.cells[e]) && row.cells[e].note" class="ah-note">— {{ row.cells[e].note }}</span>
                            </template>
                            <template v-else>—</template>
                        </td>
                        <!-- ⚠ Coloured by `better`, never by the sign. On the hit-ratio
                             row a positive difference is good and a negative one is
                             bad; on every seconds row it is the other way round. -->
                        <td v-if="comparisonEnvs.length === 2"
                            :class="row.delta && row.delta.better !== null ? (row.delta.better ? 'ah-pass' : 'ah-fail') : 'ah-na'">
                            <template v-if="row.delta">{{ deltaText(row.delta) }}</template>
                            <template v-else>—</template>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Per-check trend. One line per environment: averaging them would hide
                 whichever one is failing. -->
            <h3 class="ah-h3">Trend</h3>
            <p v-if="!checkNames.length" class="ah-none" data-testid="ah-series-none">No records in this window.</p>
            <div class="ah-sparks" v-else data-testid="ah-series">
                <div v-for="name in checkNames" :key="name" class="ah-spark" :data-testid="'ah-spark-' + name">
                    <div class="ah-spark-name">{{ name }}</div>
                    <div v-for="e in envsFor(name)" :key="e" class="ah-spark-row">
                        <span class="ah-spark-env">{{ e }}</span>
                        <svg class="ah-spark-svg" viewBox="0 0 100 24" preserveAspectRatio="none">
                            <!-- the target, so a line is readable without the numbers -->
                            <line v-if="targetY(name, e) !== null" x1="0" x2="100"
                                  :y1="targetY(name, e)" :y2="targetY(name, e)" class="ah-spark-target" />
                            <polyline :points="points(name, e)" class="ah-spark-line" />
                            <circle v-for="(p, i) in dots(name, e)" :key="i" :cx="p.x" :cy="p.y" r="1.6"
                                    :class="p.pass === false ? 'ah-dot-fail' : 'ah-dot-pass'" />
                        </svg>
                        <span class="ah-spark-last" :class="lastClass(name, e)">{{ lastValue(name, e) }}</span>
                    </div>
                </div>
            </div>

            <!-- Every record, newest first. -->
            <h3 class="ah-h3">Runs and samples</h3>
            <p v-if="!records.length" class="ah-none" data-testid="ah-records-none">No records in this window.</p>
            <table class="ah-table" v-else data-testid="ah-records">
                <thead>
                    <tr><th></th><th>Time</th><th>Env</th><th>Kind</th><th>Build</th><th>Result</th></tr>
                </thead>
                <tbody>
                    <template v-for="(r, i) in records">
                        <tr :key="r.key" class="ah-row" :class="rowClass(r)" @click="toggle(i)"
                            :data-testid="'ah-record-' + r.kind">
                            <td class="ah-caret">{{ open[i] ? '▾' : '▸' }}</td>
                            <td>{{ shortTime(r.at) }}</td>
                            <td>{{ r.env }}</td>
                            <td>{{ r.kind }}</td>
                            <td class="ah-sha">{{ r.sha || '—' }}</td>
                            <td :class="verdictClass(r)" :data-testid="'ah-verdict-' + verdictWord(r)">{{ verdictWord(r) }}</td>
                        </tr>
                        <tr v-if="open[i]" :key="r.key + ':checks'" class="ah-checks-row">
                            <td colspan="6">
                                <table class="ah-checks" data-testid="ah-checks">
                                    <thead><tr><th>Check</th><th>Target</th><th>Value</th><th>Samples</th><th>Result</th></tr></thead>
                                    <tbody>
                                        <tr v-for="c in r.checks" :key="c.name" :class="verdictClass(c)">
                                            <td>{{ c.name }}</td>
                                            <td>{{ c.op }} {{ c.target }}</td>
                                            <td>{{ c.value === null ? '—' : c.value }}</td>
                                            <td>{{ c.n === null ? '—' : c.n }}</td>
                                            <td :class="verdictClass(c)">
                                                {{ verdictWord(c) }}
                                                <!-- An unexplained n/a beats a false failure, but an
                                                     explained one beats both. Shown only when the
                                                     writer recorded a reason. -->
                                                <span v-if="isNa(c) && c.note" class="ah-note">— {{ c.note }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <a v-if="r.link && isHttp(r.link)" :href="r.link" target="_blank" rel="noopener"
                                   class="ah-link" data-testid="ah-link">Open the pipeline</a>
                                <span v-else-if="r.link" class="ah-link-plain">{{ r.link }}</span>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
    /**
     * #1886 — "Assign page health" (Super Admin).
     *
     * ed, 2026-09-10: *"I want to see evidence of those runs in a SA report page
     * with targets and results."*
     *
     * ⚠ IT DECIDES NOTHING. Every red and green on this page comes from the `pass`
     * the writer recorded — never from comparing `value` to `target` here. The page
     * exists to show what the CI job and the sampler concluded, and a second
     * opinion rendered on top of the first would make a writer's own disagreement
     * with its numbers invisible, which is exactly the finding worth seeing.
     *
     * The only arithmetic is geometry: scaling points into a sparkline viewbox.
     */
    import service from '../../services.js';
    import { mapGetters } from 'vuex';
    import { comparisonRows } from '../../assignHealthChecks';

    export default {
        name: 'AssignHealthReport',
        data() {
            return {
                loading: false,
                error: '',
                denied: false,
                days: 7,
                env: '',
                records: [],
                latestSample: {},
                latestCi: {},
                series: {},
                open: {},
            };
        },
        computed: {
            ...mapGetters(['userToken', 'userRole', 'userClientID']),
            stripEnvs() {
                // Matches the server's default so the strip and the records table
                // never disagree about what "all" means. stage- is not in it.
                return this.env ? [this.env] : ['private', 'production'];
            },
            /** Only environments that actually have a sample get a column. */
            comparisonEnvs() {
                return this.stripEnvs.filter((e) => this.latestSample[e]);
            },
            comparison() {
                return comparisonRows(this.latestSample, this.stripEnvs);
            },
            tgLabel() {
                return this.comparisonEnvs.length === 2
                    ? 'private = Private-Subdomains pair · production = production-unified pair'
                    : '';
            },
            checkNames() {
                return Object.keys(this.series);
            },
        },
        mounted() { this.load(); },
        methods: {
            load() {
                this.loading = true;
                this.error = '';
                this.denied = false;
                var q = 'days=' + this.days + (this.env ? '&env=' + this.env : '');
                service.get('superadmin/assign-health?' + q, {
                    'Authorization': this.userToken,
                    'cs3-context-role': this.userRole,
                    'cs3-context-clientId': this.userClientID,
                }).then((res) => {
                    this.loading = false;
                    var r = (res && res.data && res.data.result) || {};
                    this.records = r.records || [];
                    this.latestSample = r.latest_sample || {};
                    this.latestCi = r.latest_ci || {};
                    this.series = r.series || {};
                    this.open = {};
                }).catch((err) => {
                    this.loading = false;
                    var code = err && err.response && err.response.status;
                    if (code === 403) { this.denied = true; return; }
                    this.error = 'Could not load the health records. Please try again.';
                    console.log(err);
                });
            },

            toggle(i) { this.$set(this.open, i, !this.open[i]); },

            // ── verdicts: READ, never derived ───────────────────────────────
            /**
             * ⚠ THREE STATES, NOT TWO. `null` is "not judged" — the sampler writes
             * it for a check it could not evaluate (an environment that does not
             * carry the code, a hit ratio under the minimum reads, an ALB metric
             * with no traffic). Rendering that as a failure is how production's
             * 11:18Z record came to show seven failures nobody had ever judged.
             *
             * A false alarm on a health page is worse than no page: it teaches the
             * person reading it to discount red.
             */
            isNa(r) { return !r || r.pass === null || r.pass === undefined; },
            verdictWord(r) {
                if (this.isNa(r)) return 'n/a';
                return r.pass ? 'pass' : 'FAIL';
            },
            verdictClass(r) {
                if (this.isNa(r)) return 'ah-na';
                return r.pass ? 'ah-pass' : 'ah-fail';
            },
            rowClass(r) { return r.pass === false ? 'ah-row-fail' : ''; },
            cardClass(r) {
                // `r.pass ? … : …` is two states and put every unjudged record in
                // the red half. Not judged is its own colour.
                if (this.isNa(r)) return 'ah-card-na';
                return r.pass ? 'ah-card-pass' : 'ah-card-fail';
            },
            failedCount(r) {
                if (!r) return 0;
                return r.checks.filter(function (c) { return c.pass === false; }).length;
            },
            /** Judged is the denominator: "1 of 2 failing" beside 5 n/a is a lie about 5. */
            judgedCount(r) {
                if (!r) return 0;
                return r.checks.filter(function (c) { return c.pass === true || c.pass === false; }).length;
            },
            naCount(r) {
                if (!r) return 0;
                return r.checks.length - this.judgedCount(r);
            },

            // ── sparklines: the only arithmetic here is geometry ────────────
            pointsFor(name, env) {
                return ((this.series[name] || {})[env]) || [];
            },
            envsFor(name) { return Object.keys(this.series[name] || {}); },
            /** Scale so the target line is always inside the box, even when every value clears it. */
            bounds(name, env) {
                var pts = this.pointsFor(name, env);
                var vals = pts.map(function (p) { return Number(p.value); }).filter(function (v) { return isFinite(v); });
                var t = pts.length ? Number(pts[0].target) : NaN;
                if (isFinite(t)) vals.push(t);
                if (!vals.length) return null;
                var min = Math.min.apply(null, vals);
                var max = Math.max.apply(null, vals);
                if (max === min) { max = min + 1; }
                return { min: min, max: max };
            },
            y(name, env, value) {
                var b = this.bounds(name, env);
                if (!b || !isFinite(Number(value))) return null;
                // 22 → 2 so the extremes are not clipped by the stroke width.
                return 22 - ((Number(value) - b.min) / (b.max - b.min)) * 20;
            },
            targetY(name, env) {
                var pts = this.pointsFor(name, env);
                return pts.length ? this.y(name, env, pts[0].target) : null;
            },
            dots(name, env) {
                var pts = this.pointsFor(name, env);
                var self = this;
                return pts.map(function (p, i) {
                    return {
                        x: pts.length === 1 ? 50 : (i / (pts.length - 1)) * 100,
                        y: self.y(name, env, p.value),
                        pass: p.pass,
                    };
                }).filter(function (p) { return p.y !== null; });
            },
            points(name, env) {
                return this.dots(name, env).map(function (p) { return p.x + ',' + p.y; }).join(' ');
            },
            lastValue(name, env) {
                var pts = this.pointsFor(name, env);
                return pts.length ? pts[pts.length - 1].value : '—';
            },
            lastClass(name, env) {
                var pts = this.pointsFor(name, env);
                return pts.length ? this.verdictClass(pts[pts.length - 1]) : 'ah-na';
            },

            // ── display ────────────────────────────────────────────────────
            cellValue(c) {
                return (c && c.value !== null && c.value !== undefined && c.value !== '') ? c.value : '—';
            },
            /**
             * ⚠ Signed from the first column, and the WORD carries the judgement.
             * A bare "−3.51" reads as worse to anyone scanning, and on the
             * hit-ratio row that would be exactly backwards.
             */
            deltaText(delta) {
                if (!delta) { return '—'; }
                if (delta.value === 0) { return 'same'; }
                var n = Math.round(delta.value * 100) / 100;

                return (n > 0 ? '+' : '') + n + (delta.better === null ? '' : (delta.better ? ' better' : ' worse'));
            },
            shortTime(at) {
                if (!at) return '—';
                return String(at).replace('T', ' ').replace('Z', ' UTC');
            },
            /** `link` may be an s3:// key, which is not something to render as an anchor. */
            isHttp(link) { return /^https?:\/\//.test(String(link || '')); },
        },
    };
</script>

<style scoped>
    .ah { padding-top: 60px; padding-left: 20px; padding-right: 20px; }
    .ah-lede { color: #6b6b6b; margin: 4px 0 16px; }
    .ah-controls { display: flex; gap: 16px; align-items: center; margin-bottom: 16px; font-size: 13px; }
    .ah-refresh { padding: 4px 12px; }
    .ah-denied, .ah-error { color: #a33; font-size: 14px; }
    .ah-none { color: #999; font-size: 13px; }
    .ah-h3 { margin: 22px 0 8px; font-size: 15px; font-weight: 600; }

    .ah-strip { display: flex; gap: 14px; flex-wrap: wrap; }
    .ah-card { flex: 1 1 260px; border: 1px solid #ddd; border-left-width: 5px; border-radius: 4px; padding: 10px 14px; }
    .ah-card-pass { border-left-color: #4a9d5b; }
    .ah-card-fail { border-left-color: #c0392b; }
    .ah-card-na { border-left-color: #bbb; }
    .ah-card-env { font-size: 12px; text-transform: uppercase; color: #777; }
    .ah-card-verdict { font-size: 20px; font-weight: 600; }
    .ah-card-meta { font-size: 12px; color: #777; }

    .ah-sparks { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; }
    .ah-spark { border: 1px solid #eee; border-radius: 4px; padding: 8px 10px; }
    .ah-spark-name { font-size: 12px; font-weight: 600; margin-bottom: 4px; }
    .ah-spark-row { display: flex; align-items: center; gap: 8px; }
    .ah-spark-env { font-size: 11px; color: #888; width: 74px; }
    .ah-spark-svg { flex: 1; height: 24px; }
    .ah-spark-line { fill: none; stroke: #4a76a8; stroke-width: 1.2; vector-effect: non-scaling-stroke; }
    .ah-spark-target { stroke: #c0392b; stroke-width: 1; stroke-dasharray: 3 3; opacity: .55; vector-effect: non-scaling-stroke; }
    .ah-dot-pass { fill: #4a9d5b; }
    .ah-dot-fail { fill: #c0392b; }
    .ah-spark-last { font-size: 12px; width: 60px; text-align: right; }

    .ah-table { width: 100%; font-size: 13px; border-collapse: collapse; }
    .ah-table th { text-align: left; border-bottom: 1px solid #ddd; padding: 4px 8px; font-weight: 600; }
    .ah-row { cursor: pointer; }
    .ah-row td { padding: 4px 8px; border-bottom: 1px solid #f2f2f2; }
    .ah-row-fail { background: #fdf1f0; }
    .ah-caret { width: 18px; color: #999; }
    .ah-sha { font-family: monospace; font-size: 12px; }
    .ah-checks-row td { padding: 8px 8px 14px 26px; background: #fafafa; }
    .ah-checks { width: 100%; font-size: 12px; border-collapse: collapse; }
    .ah-checks th { text-align: left; color: #777; font-weight: 600; padding: 2px 8px 2px 0; }
    .ah-checks td { padding: 2px 8px 2px 0; }
    .ah-pass { color: #2f7d3f; }
    .ah-fail { color: #c0392b; font-weight: 600; }
    .ah-na { color: #999; }
    .ah-note { color: #999; font-style: italic; }
    .ah-compare td { vertical-align: top; }
    .ah-desc { display: block; color: #888; font-size: 11px; }
    .ah-target { color: #777; white-space: nowrap; }
    .ah-verdict-word { font-size: 11px; margin-left: 4px; }
    .ah-median { display: block; font-size: 11px; }
    .ah-h3-note { font-weight: 400; color: #888; font-size: 11px; margin-left: 8px; }
    .ah-link { font-size: 12px; }
    .ah-link-plain { font-size: 12px; color: #999; font-family: monospace; }
</style>
